import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react'
import { useMutation } from '@tanstack/react-query'
import { changeIndexRuntimeSetting } from '~/services/organizationService'
import { queryClient, QueryKeys } from '~/services/queryClient'
import { globalEventEmitter, AppEvents } from '~/utils/shared'
import { IndexRuntimeSettings } from '~/types/ResponseType'
import { JavaVersion, MavenVersion, MuleRuntimeVersion } from '~/constants/settings'
import { FlatIndex, useIndexesContext } from '~/contexts/IndexesContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'

export interface UseRuntimeSettingsResult {
  selectedIndex: FlatIndex | null
  setSelectedIndex: Dispatch<SetStateAction<FlatIndex | null>>
  drawerRuntimeSetting: IndexRuntimeSettings
  updateDrawerRuntimeSetting: (
    updater: (prev: IndexRuntimeSettings) => IndexRuntimeSettings
  ) => void
  handleRuntimeSettingSelectChange: (indexRuntimeSettings: IndexRuntimeSettings) => Promise<boolean>
  handleRemoveSettingsXML: (isSettingsSecurityXML?: boolean) => Promise<void>
  getRuntimeSettingForIndex: (index: FlatIndex) => IndexRuntimeSettings
}

export function useRuntimeSettings(): UseRuntimeSettingsResult {
  const [selectedIndex, setSelectedIndex] = useState<FlatIndex | null>(null)
  const [drawerRuntimeSetting, setDrawerRuntimeSetting] = useState<IndexRuntimeSettings>({
    indexId: 0,
    java_version: JavaVersion['8_0'],
    maven_version: MavenVersion['3_5'],
    mule_runtime_version: MuleRuntimeVersion['4_8'],
    settings_xml_raw_txt: '',
    settings_security_xml_raw_txt: '',
    vm_args: ''
  })

  // Helper function to get runtime settings for a specific index
  const getRuntimeSettingForIndex = useCallback(
    (index: FlatIndex): IndexRuntimeSettings => ({
      indexId: index.id,
      java_version: index.java_version,
      maven_version: index.maven_version,
      mule_runtime_version: index.mule_runtime_version,
      settings_xml_raw_txt: index.settings_xml_raw_txt || '',
      settings_security_xml_raw_txt: index.settings_security_xml_raw_txt || '',
      vm_args: index.vm_args
    }),
    []
  )

  // Update runtime settings when selected index changes
  useEffect(() => {
    if (selectedIndex) {
      const runtimeSetting = getRuntimeSettingForIndex(selectedIndex)
      setDrawerRuntimeSetting(runtimeSetting)
    }
  }, [selectedIndex, getRuntimeSettingForIndex])

  const { updateIndexRuntimeSetting: applyRuntimeSettingToIndex } = useIndexesContext()
  const { currentWorkspace } = useWorkspaceContext()

  const { mutateAsync: updateIndexRuntimeSetting } = useMutation({
    mutationFn: changeIndexRuntimeSetting,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.Indexes })
      globalEventEmitter.emit(AppEvents.IndexesUpdated)
    }
  })

  const handleRuntimeSettingSelectChange = useCallback(
    async (indexRuntimeSettings: IndexRuntimeSettings) => {
      const indexId = indexRuntimeSettings.indexId

      if (!currentWorkspace?.uuid) {
        console.error('No workspace selected')
        return false
      }

      try {
        const payload = {
          workspaceUuid: currentWorkspace.uuid,
          index_id: indexId,
          runtimeSetting: indexRuntimeSettings
        }
        await updateIndexRuntimeSetting(payload)
        applyRuntimeSettingToIndex(indexId, {
          java_version: indexRuntimeSettings.java_version,
          maven_version: indexRuntimeSettings.maven_version,
          mule_runtime_version: indexRuntimeSettings.mule_runtime_version,
          settings_xml_raw_txt: indexRuntimeSettings.settings_xml_raw_txt,
          settings_security_xml_raw_txt: indexRuntimeSettings.settings_security_xml_raw_txt,
          vm_args: indexRuntimeSettings.vm_args
        })
        return true
      } catch (error) {
        console.error('Failed to update runtime settings', error)
        return false
      }
    },
    [updateIndexRuntimeSetting, applyRuntimeSettingToIndex, currentWorkspace]
  )

  const updateDrawerRuntimeSetting = useCallback(
    (updater: (prev: IndexRuntimeSettings) => IndexRuntimeSettings) => {
      setDrawerRuntimeSetting(updater)
    },
    []
  )

  const handleRemoveSettingsXML = useCallback(
    async (isSettingsSecurityXML = false) => {
      try {
        if (!selectedIndex) return

        setDrawerRuntimeSetting((prev: IndexRuntimeSettings) => ({
          ...prev,
          settings_xml_raw_txt: isSettingsSecurityXML ? prev.settings_xml_raw_txt : '',
          settings_security_xml_raw_txt: isSettingsSecurityXML
            ? ''
            : prev.settings_security_xml_raw_txt,
          settings_xml_filename: isSettingsSecurityXML ? prev.settings_xml_filename : '',
          settings_security_xml_filename: isSettingsSecurityXML
            ? ''
            : prev.settings_security_xml_filename
        }))

        // Clear corresponding file input value to ensure same file can be reselected
        const fileInput = document.getElementById(
          isSettingsSecurityXML ? 'securityFileUpload' : 'fileUpload'
        ) as HTMLInputElement
        if (fileInput) {
          fileInput.value = ''
        }
      } catch (error) {
        console.error('Error removing settings XML:', error)
      }
    },
    [selectedIndex]
  )

  return {
    selectedIndex,
    setSelectedIndex,
    drawerRuntimeSetting,
    updateDrawerRuntimeSetting,
    handleRuntimeSettingSelectChange,
    handleRemoveSettingsXML,
    getRuntimeSettingForIndex
  }
}
